import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  CaasComponent,
  CaasComponents,
  CaasLayout,
  TranslatedComponents,
  CaasMainSection,
} from '../../../shared/interfaces/caas/caas.interface';
import { AccordionItem } from '../../../shared/interfaces/accordion-item.interface';
import { UtilitiesCards } from '../../../shared/interfaces/utilities-cards.interface';
import { StaticGraphicComponent } from '../../molecules/static-graphic/static-graphic.component';
import { HighlightcardListComponent } from '../../molecules/highlightcard-list/highlightcard-list.component';
import { TextBlockComponent } from '../../atoms/text-block/text-block.component';
import { UtilitiesCardsComponent } from '../../molecules/utilities-cards/utilities-cards.component';
import { PodcastComponent } from '../accordion/podcast/podcast.component';
import { MaterialDownloadComponent } from '../../molecules/material-download/material-download.component';
import { LogoWallComponent } from '../../molecules/logo-wall/logo-wall.component';
import { CrossReferencingComponent } from '../cross-referencing/cross-referencing.component';
import { PrefooterComponent } from '../prefooter/prefooter.component';
import { CommonModule } from '@angular/common';
import { TeaserContainerComponent } from '../teaser-container/teaser-container.component';
import { SliderComponent } from '../slider/slider.component';
import { ContactModuleComponent } from '../../molecules/contact-module/contact-module.component';
import { InfoTeasersComponent } from '../../molecules/info-teasers/info-teasers.component';
import { TermineComponent } from '../accordion/termine/termine.component';
import { QuoteComponent } from '../../molecules/quote/quote.component';
import { AccordionComponent } from '../accordion/standard/accordion.component';
import { HelpDisruptorComponent } from '../help-disruptor/help-disruptor.component';
import { ManipulateDataService } from '../../../services/translate.service';
import { Headlines } from '../../../shared/enums/shared.enums';
import {
  PodcastItems,
  PodcastPlatforms,
} from '../../../shared/interfaces/podcast-item.interface';
import { News, NewsFilter } from '../../../shared/interfaces/news.interface';
import { PodcastService } from '../../../services/podcast.service';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { ImagesService } from '../../../services/images.service';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { TagsService } from '../../../services/tags.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';

@Component({
  selector: 'hdi-section',
  standalone: true,
  templateUrl: './section.component.html',
  styleUrls: ['./section.component.scss'],
  imports: [
    CommonModule,
    StaticGraphicComponent,
    HighlightcardListComponent,
    TextBlockComponent,
    UtilitiesCardsComponent,
    PodcastComponent,
    MaterialDownloadComponent,
    LogoWallComponent,
    CrossReferencingComponent,
    PrefooterComponent,
    TeaserContainerComponent,
    SliderComponent,
    ContactModuleComponent,
    InfoTeasersComponent,
    TermineComponent,
    QuoteComponent,
    AccordionComponent,
    HelpDisruptorComponent,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class SectionComponent implements OnChanges {
  @Input() backgroundColor: 'white' | 'grey' = 'white';
  @Input() id: string = '';
  @Input() paddingTop?: boolean = false;
  @Input() paddingBottom?: boolean = false;
  @Input() paddingBlock?: boolean = true;
  @Input() mainSection?: CaasMainSection;
  @Input() podcastData: {
    podcasts: PodcastItems[];
    platforms: PodcastPlatforms[];
  } = { podcasts: [], platforms: [] };
  @Input() news: News[] = [];
  @Input() newsFilters: NewsFilter[] = [];
  @Input() tags: { name: string; identifier: string }[] = [];
  @Input() eventsData: CaasLayout[] = [];
  @Input() imagesMetaData: ImageMetaData[] = [];

  public accordionItems: AccordionItem[] = [];
  public utilitiesCardsList: UtilitiesCards[] = [];
  public materialDownloadInclude: boolean = false;
  public materialDownloadDeepLinks: CaasComponents['value'] | undefined;
  public translatedComponents: TranslatedComponents = {
    news: [],
    newsFilters: [],
    texts: [],
    images: [],
    highlights: [],
    termines: [],
    helpdisruptors: [],
    teasers: [],
    prefooters: [],
    downloads: [],
    deeplinks: [],
    trustedAwards: [],
    contacts: [],
    podcasts: [],
    podcastPlatforms: [],
    explanations: [],
    linktiles: [],
    events: [],
    accordeons: [],
    quotes: [],
    sliderQuotes: [],
  };
  orderedComponents: CaasComponent[] = [];
  Headlines = Headlines;

  constructor(
    private readonly manipulateDataService: ManipulateDataService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly podcastService: PodcastService,
    private readonly router: Router,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly imagesService: ImagesService,
    private readonly tagsService: TagsService,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Handle podcast data changes
    if (changes['podcastData'] && this.podcastData) {
      this.podcastService.setPodcasts(this.podcastData.podcasts);
      this.podcastService.setPlatforms(this.podcastData.platforms);
    }

    if (changes['tags']) {
      this.tagsService.setTags(changes['tags'].currentValue);
    }

    // Handle news data changes
    if (changes['news']?.currentValue?.length) {
      this.manipulateDataService.setNews(changes['news'].currentValue);
    }

    if (changes['newsFilters']?.currentValue?.length) {
      this.manipulateDataService.setNewsFilters(
        changes['newsFilters'].currentValue
      );
    }

    // Handle images metadata changes
    if (changes['imagesMetaData']) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }

    // Handle mainSection changes
    if (changes['mainSection'] && this.mainSection) {
      try {
        this.translatedComponents =
          this.manipulateDataService.translateComponents(this.mainSection);

        safeAssign(
          () => {
            this.orderedComponents =
              this.mainSection?.formData?.st_items?.value || [];
          },
          'ngOnChanges.orderedComponents',
          this.errorHandler
        );

        safeAssign(
          () => {
            if (this.mainSection) {
              this.accordionItems =
                this.manipulateDataService.getAccordionItems(this.mainSection);
            }
          },
          'ngOnChanges.accordionItems',
          this.errorHandler
        );

        safeAssign(
          () => {
            if (this.mainSection) {
              this.utilitiesCardsList =
                this.manipulateDataService.getUtilitiesCardsList(
                  this.mainSection
                );
            }
          },
          'ngOnChanges.utilitiesCardsList',
          this.errorHandler
        );

        safeAssign(
          () => {
            if (this.mainSection) {
              this.materialDownloadInclude =
                this.manipulateDataService.checkMaterialDownloadInclude(
                  this.mainSection
                );
            }
          },
          'ngOnChanges.materialDownloadInclude',
          this.errorHandler
        );

        safeAssign(
          () => {
            if (this.materialDownloadInclude && this.mainSection) {
              this.materialDownloadDeepLinks =
                this.manipulateDataService.getMaterialDownloadDeepLinks(
                  this.mainSection
                );
            }
          },
          'ngOnChanges.materialDownloadDeepLinks',
          this.errorHandler
        );

        // Set route links for navigation
        this.caasRoutesService.setRouteLinks(
          this.router.config
            .filter((route) => route.path)
            .map((route) => ({
              identifier: route.data?.['pageIdentifier'],
              path: route.path || '',
            }))
        );

        this.cdRef.detectChanges();
      } catch (error) {
        console.error('Error during mainSection processing:', error);
      }
    }
  }
}
